import { StaticImageData } from 'next/image';
import { DocumentTextIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import card1 from './card1.jpg';
import card2 from './card2.jpg';
import card3 from './card4.jpg';
import card4 from './card3.jpg';
import card5 from './card5.jpg';
import aiNativeSdlcImage from './ai-native-sdlc.jpg';
import { aiNativeSdlcContent } from '@/sections/resources/ebooks-and-whitepapers/ai-native-sdlc/content';
import {
  AI_NATIVE_SDLC_DATE_PUBLISHED,
  AI_NATIVE_SDLC_PATH,
  AI_NATIVE_SDLC_STATUS,
  type WhitepaperStatus,
} from '@/sections/resources/ebooks-and-whitepapers/ai-native-sdlc/publish-status';

export type Resource = {
  title: string;
  description: string;
  type: 'whitepaper' | 'ebook';
  typeLabel: 'Whitepaper' | 'Ebook';
  color: 'gradle-blue' | 'core-success';
  link: string;
  icon: React.ReactNode;
  image: StaticImageData;
  /** The image already shows the title (no overlaid title, no dimming). */
  imageHasTitle?: boolean;
  publishedDate: string;
};

const CATALOG: Resource[] = [
  {
    title: 'Flaky Tests: The Productive Developer’s Guide',
    description:
      'Learn practical strategies for minimizing the negative impact of flaky tests and fostering a culture of test reliability.',
    type: 'whitepaper',
    typeLabel: 'Whitepaper',
    color: 'gradle-blue',
    link: '/resources/ebooks-and-whitepapers/flaky-tests/',
    icon: <DocumentTextIcon className="size-6" />,
    image: card1,
    publishedDate: '2025-05-20',
  },
  {
    title: 'The Developer Productivity Engineering Handbook',
    description: 'Get the complete guide to Developer Productivity Engineering for practitioners.',
    type: 'ebook',
    typeLabel: 'Ebook',
    color: 'core-success',
    link: '/developer-productivity-engineering/handbook/',
    icon: <BookOpenIcon className="size-6" />,
    image: card2,
    publishedDate: '2020-05-30',
  },
  {
    title: 'The Executive’s Guide to DPE',
    description: 'Translate your business goals into digital strategy with a new approach and new solutions.',
    type: 'whitepaper',
    typeLabel: 'Whitepaper',
    color: 'gradle-blue',
    link: '/executives-guide-to-developer-productivity-engineering/',
    icon: <DocumentTextIcon className="size-6" />,
    image: card3,
    publishedDate: '2021-05-30',
  },
  {
    title: 'The Developer Productivity Engineering Maturity Model',
    description:
      'Your guide to performing a DPE maturity level self-assessment and creating a plan for DPE excellence.',
    type: 'ebook',
    typeLabel: 'Ebook',
    color: 'core-success',
    link: '/developer-productivity-engineering/developer-productivity-engineering-maturity-model/',
    icon: <BookOpenIcon className="size-6" />,
    image: card4,
    publishedDate: '2023-05-29',
  },
  {
    title: "GenAI Won't Replace Your Continuous Delivery Pipeline—It Will Stress It",
    description:
      'Generative AI is transforming software engineering, offering rapid innovation but also introducing an array of new challenges to your delivery pipeline.',
    type: 'whitepaper',
    typeLabel: 'Whitepaper',
    color: 'gradle-blue',
    link: '/resources/ebooks-and-whitepapers/gen-ai-will-stress-continuous-delivery',
    icon: <DocumentTextIcon className="size-6" />,
    image: card5,
    publishedDate: '2025-05-30',
  },
];

/**
 * "A Model of the AI-Native SDLC" whitepaper: its title, the lede's first sentence and a card
 * image like the share image (the cover, with its title, over the hero mosaic) with the cover
 * smaller so it survives the card's widest crop (~2.9:1 in the 2-column layout). The card shows
 * no overlaid title.
 */
export const aiNativeSdlcResource = (datePublished: string): Resource => ({
  title: aiNativeSdlcContent.title,
  description: aiNativeSdlcContent.summary,
  type: 'whitepaper',
  typeLabel: 'Whitepaper',
  color: 'gradle-blue',
  link: AI_NATIVE_SDLC_PATH,
  icon: <DocumentTextIcon className="size-6" />,
  image: aiNativeSdlcImage,
  imageHasTitle: true,
  publishedDate: datePublished,
});

/** The catalog (and the page's ItemList JSON-LD), newest first: the order both show. The AI-native
 * SDLC paper joins only once its status is `published` and dated (publish-status.ts), as on the
 * homepage's What's new. */
export function buildResources(paperStatus: WhitepaperStatus, paperDate: string | null): Resource[] {
  const list = paperStatus === 'published' && paperDate ? [...CATALOG, aiNativeSdlcResource(paperDate)] : CATALOG;
  return [...list].sort((a, b) => b.publishedDate.localeCompare(a.publishedDate));
}

export const resources = buildResources(AI_NATIVE_SDLC_STATUS, AI_NATIVE_SDLC_DATE_PUBLISHED);
